import { createHook, createStore } from 'react-sweet-state'

export interface IClipboardItem {
    ui: string
    selectedYear: number
    recordName: string
    relatedDescriptorUi?: string
}

type StorageData = {
    descriptors: IClipboardItem[]
    qualifiers: IClipboardItem[]
}

const STORAGE_KEY = 'clipboardStorage'

function getInitialStorage(): StorageData {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : { descriptors: [], qualifiers: [] }
    } catch {
        return { descriptors: [], qualifiers: [] }
    }
}

function saveStorage(data: StorageData) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

type State = StorageData
const initialState: State = getInitialStorage()

const actions = {
    addItem:
        (type: keyof StorageData, item: IClipboardItem) =>
        ({ setState, getState }) => {
            const current = getState()[type]
            if (current.some((i) => i.ui === item.ui)) return
            const updated = [...current, item]
            const nextState = { ...getState(), [type]: updated }
            saveStorage(nextState)
            setState(nextState)
        },

    removeItem:
        (type: keyof StorageData, ui: string) =>
        ({ setState, getState }) => {
            const updated = getState()[type].filter((item) => item.ui !== ui)
            const nextState = { ...getState(), [type]: updated }
            saveStorage(nextState)
            setState(nextState)
        },

    clearItems:
        (type: keyof StorageData) =>
        ({ setState, getState }) => {
            const nextState = { ...getState(), [type]: [] }
            saveStorage(nextState)
            setState(nextState)
        },

    syncFromStorage:
        () =>
        ({ setState }) => {
            const updated = getInitialStorage()
            setState(updated)
        },
    updateItem:
        (
            type: keyof StorageData,
            ui: string,
            updates: Partial<Pick<IClipboardItem, 'format' | 'extension'>>
        ) =>
        ({ setState, getState }) => {
            const updated = getState()[type].map((item) =>
                item.ui === ui ? { ...item, ...updates } : item
            )
            const nextState = { ...getState(), [type]: updated }
            saveStorage(nextState)
            setState(nextState)
        },
}

const ClipboardStorageStore = createStore<State, typeof actions>({
    name: 'ClipboardStorage',
    initialState,
    actions,
})

export const useClipboardStorageState = createHook(ClipboardStorageStore)
